import { DateFormatType, formatNullableDate } from "~/common/utils/DateFormatter";
import Parameter from "~/common/components/Parameter";
import { InfoBall } from "~/features/configurator/Linear/components/LinearInfo";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { useTranslation } from "react-i18next";
import { styled } from "~/common/mui";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import type { QuoteDetailsMO } from "~/features/orders/common/components/v2/models";
import { QuoteIsBundleInfoModal } from "~/features/orders/quoteDetails/QuoteIsBundleInfoModal";

const SummaryRoot = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(8, minmax(0, 1fr))",
  columnGap: "var(--gutter)",
  rowGap: theme.spacing(2),
}));

const ParamsGrid = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "subgrid",
  rowGap: theme.spacing(1),
  gridColumnEnd: "span 6",
  "& > *": {
    gridColumnEnd: "span 2",
  },
}));

const CommentBlock = styled("div")({
  gridColumn: "1 / -1",
});

interface OrderSummaryProps {
  className?: string;
  quote: QuoteDetailsMO;
}

export const QuoteDetailsSummary = ({ quote, className }: OrderSummaryProps) => {
  const { t } = useTranslation();

  const { val: showBundlePopup, setTrue: openBundlePopup, setFalse: closeBundlePopup } = useBooleanState(false);

  const canSeeComments = userHasPermission(Permission.ADD_VIEW_HEADER_LEVEL_COMMENTS);
  const showComment = quote.comment && canSeeComments;

  return (
    <SummaryRoot className={className}>
      <ParamsGrid>
        <Parameter data-test-id={"quote-status"} label={t("quoteDetailsPage.summary.quoteStatus")}>
          {t(`codes.quoteStatus.${quote.status}`)}
        </Parameter>
        <Parameter data-test-id={"order-attainment"} label={t("quoteDetailsPage.summary.orderAttainment")}>
          {quote.orderAttainment}
        </Parameter>
        <Parameter data-test-id={"date-entered"} label={t("quoteDetailsPage.summary.dateEntered")}>
          {formatNullableDate(quote.datePlaced, DateFormatType.DATE)}
        </Parameter>

        <Parameter data-test-id={"account-number"} label={t("quoteDetailsPage.summary.accNo")}>
          {quote.accNo}
        </Parameter>
        <Parameter data-test-id={"order-fullfilment"} label={t("quoteDetailsPage.summary.orderFullfilment")}>
          {quote.orderFulfilment}
        </Parameter>
        <Parameter data-test-id={"date-expired"} label={t("quoteDetailsPage.summary.dateExpired")}>
          {formatNullableDate(quote.dateExpired, DateFormatType.DATE)}
        </Parameter>

        <Parameter data-test-id={"customer-ref-number"} label={t("quoteDetailsPage.summary.customerRefNo")}>
          {quote.poNumber}
        </Parameter>
        <Parameter data-test-id={"quote-number"} label={t("quoteDetailsPage.summary.quoteNumber")}>
          {quote.soNumber}
        </Parameter>
        <Parameter data-test-id={"quote-type"} label={t("quoteDetailsPage.summary.quoteType")}>
          <div className="d-flex flex-align-center">
            {t(quote.quoteType)}
            {quote.isBundledQuote && <InfoBall className={"icon-margin-l"} onClick={openBundlePopup} />}
            {showBundlePopup && <QuoteIsBundleInfoModal onClose={closeBundlePopup} />}
          </div>
        </Parameter>
      </ParamsGrid>
      {showComment && (
        <CommentBlock>
          <Parameter data-test-id={"comment"} label={t("order.information.header.comment")}>
            {quote.comment}
          </Parameter>
        </CommentBlock>
      )}
    </SummaryRoot>
  );
};
